import {
  Box,
  Button,
  ButtonProps,
  Combobox,
  Group,
  Loader,
  ScrollArea,
  Select,
  Text,
  useCombobox,
} from "@mantine/core";
import { IconPhoto, IconPlus } from "@tabler/icons-react";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { API_ROUTE } from "../../const/apiRoute";
import { convertImageDomain } from "../../const/converhandle";
import { api } from "../../library/axios";
import { ArticleCategoryRef } from "./homeBlockConfig";
import classes from "./homeBlock.module.css";

// Ô chọn lấy dữ liệu từ các danh sách sẵn có trong CMS. Gọi thẳng api để lỗi không bật thông báo mỗi lần gõ.

// Ảnh banner/bài viết có thể là URL đủ hoặc đường dẫn trên CDN web (giống màn Quản lý banner)
export const cdnImage = (url: unknown): string | null => {
  if (typeof url !== "string" || !url.trim()) return null;
  const value = url.trim();
  if (/^https?:\/\//i.test(value)) return convertImageDomain(value);
  if (value.includes("192.168.3.96:9500")) return value.replace("192.168.3.96:9500", "image-dev.hacom.vn");
  if (value.includes("192.168.2.17:9500")) return value.replace("192.168.2.17:9500", "image-dev.hacom.vn");
  if (value.includes("image-dev.hacom.vn")) return value;
  return `https://cdn-files.hacom.vn/hacom/cdn/web${value.startsWith("/") ? "" : "/"}${value}`;
};

const listOf = (res: any): any[] => (Array.isArray(res?.data?.data) ? res.data.data : []);

const formatDate = (value: unknown) => {
  if (typeof value !== "string" || !value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("vi-VN");
};

// ---------- Banner (TblBanner) ----------

export type BannerOption = {
  id: number;
  name: string;
  image: string | null;
  link: string;
  schedule: string;
};

const toBanner = (item: any): BannerOption | null => {
  if (typeof item?.id !== "number") return null;
  const from = formatDate(item.fromDate);
  const to = formatDate(item.toDate);
  return {
    id: item.id,
    name: String(item.name ?? item.code ?? `Banner ${item.id}`).trim(),
    image: cdnImage(item.fileBanner),
    link: typeof item.bannerUrlTarget === "string" ? item.bannerUrlTarget : "",
    schedule: from || to ? `${from || "…"} - ${to || "…"}` : "Luôn hiển thị",
  };
};

export const searchBanners = async (keyword: string) => {
  const res = await api.get(
    `${API_ROUTE.GET_LIST_BANNER}?Skip=0&Take=30${keyword ? `&KeySearch=${encodeURIComponent(keyword)}` : ""}`
  );
  return listOf(res).map(toBanner).filter((b): b is BannerOption => !!b);
};

const bannerRequests = new Map<number, Promise<BannerOption | null>>();

// Thông tin banner đã chọn (ảnh, link, lịch) theo id, mỗi id gọi một lần
export const useBannerInfo = (id: number | null) => {
  const [loaded, setLoaded] = useState<{ id: number; info: BannerOption | null } | null>(null);

  useEffect(() => {
    if (id === null) return;
    let request = bannerRequests.get(id);
    if (!request) {
      request = api
        .get(`${API_ROUTE.DETAIL_BANNER}${id}`)
        .then((res) => toBanner(res.data?.data))
        .catch(() => {
          bannerRequests.delete(id);
          return null;
        });
      bannerRequests.set(id, request);
    }
    let alive = true;
    request.then((info) => alive && setLoaded({ id, info }));
    return () => {
      alive = false;
    };
  }, [id]);

  return loaded && loaded.id === id ? loaded.info : null;
};

// Banner tìm được cũng dùng làm thông tin hiển thị, khỏi gọi lại chi tiết
export const rememberBanner = (banner: BannerOption) =>
  bannerRequests.set(banner.id, Promise.resolve(banner));

// ---------- Bài viết (TblArticle) ----------

export type ArticleOption = { id: number; title: string; image: string | null; date: string };

export const searchArticles = async (keyword: string) => {
  const res = await api.get(
    `${API_ROUTE.GET_LIST_ARTICLE_LIST}?Skip=0&Take=30${keyword ? `&KeySearch=${encodeURIComponent(keyword)}` : ""}`
  );
  return listOf(res)
    .filter((item) => typeof item?.id === "number")
    .map(
      (item): ArticleOption => ({
        id: item.id,
        title: String(item.title ?? `Bài viết ${item.id}`).trim(),
        image: cdnImage(item.thumnail),
        date: formatDate(item.postedDate),
      })
    );
};

// ---------- Chuyên mục bài viết (TblArticleCategory) ----------

let categoryRequest: Promise<{ id: number; name: string }[] | null> | null = null;

const loadArticleCategories = () => {
  if (!categoryRequest) {
    categoryRequest = api
      .get(`${API_ROUTE.GET_LIST_ARTICLE_CATEGORY}?Skip=0&Take=1000`)
      .then((res) =>
        listOf(res)
          .filter((item) => typeof item?.id === "number")
          .map((item) => ({ id: item.id as number, name: String(item.name ?? "").trim() }))
      )
      .catch(() => {
        categoryRequest = null;
        return null;
      });
  }
  return categoryRequest;
};

type ArticleCategorySelectProps = {
  value: ArticleCategoryRef;
  onChange: (value: ArticleCategoryRef) => void;
  label?: string;
  ariaLabel?: string;
};

// Chọn chuyên mục bài viết; dữ liệu mẫu chỉ có tên thì tự khớp theo tên khi tải xong
export const ArticleCategorySelect = ({ value, onChange, label, ariaLabel }: ArticleCategorySelectProps) => {
  const [items, setItems] = useState<{ id: number; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const matchedRef = useRef(false);

  useEffect(() => {
    let alive = true;
    loadArticleCategories().then((result) => {
      if (!alive) return;
      setItems(result ?? []);
      setFailed(result === null);
      setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (matchedRef.current || loading || value.id !== null || !value.name) return;
    matchedRef.current = true;
    const wanted = value.name.trim().toLowerCase();
    const found = items.find((c) => c.name.toLowerCase() === wanted);
    if (found) onChange({ id: found.id, name: found.name });
  }, [items, loading, value, onChange]);

  const data = useMemo(() => items.map((c) => ({ value: String(c.id), label: c.name })), [items]);

  return (
    <Select
      label={label}
      aria-label={ariaLabel}
      placeholder={
        loading
          ? "Đang tải chuyên mục..."
          : failed
            ? "Không tải được chuyên mục"
            : value.id === null && value.name
              ? `Chưa khớp: ${value.name}`
              : "Chọn chuyên mục"
      }
      data={data}
      value={value.id === null ? null : String(value.id)}
      onChange={(v) => {
        const found = items.find((c) => String(c.id) === v);
        onChange(found ? { id: found.id, name: found.name } : { id: null, name: "" });
      }}
      searchable
      clearable
      disabled={loading}
      error={failed ? "Không gọi được API chuyên mục bài viết" : undefined}
      nothingFoundMessage="Không có chuyên mục khớp"
      comboboxProps={{ withinPortal: true, keepMounted: false }}
    />
  );
};

// ---------- Ảnh khách hàng (CustomerImages) ----------

export type CustomerImageOption = {
  id: number;
  itemCode: string;
  branchName: string;
  urls: string[];
};

export const searchCustomerImages = async (keyword: string) => {
  const res = await api.get(
    `/CustomerImages/get-list?Skip=0&Take=30&Active=true${keyword ? `&KeySearch=${encodeURIComponent(keyword)}` : ""}`
  );
  return listOf(res)
    .filter((item) => typeof item?.id === "number")
    .map(
      (item): CustomerImageOption => ({
        id: item.id,
        itemCode: String(item.itemCode ?? ""),
        branchName: String(item.branchName ?? ""),
        // Màn Ảnh khách hàng dùng nguyên các URL này (đã là URL đủ)
        urls: String(item.urls ?? "")
          .split(",")
          .map((u) => u.trim())
          .filter(Boolean)
          .map((u) => convertImageDomain(u)),
      })
    );
};

// ---------- Ô chọn có tìm kiếm dùng chung ----------

type SearchPickerProps<T> = {
  buttonLabel: string;
  searchPlaceholder: string;
  search: (keyword: string) => Promise<T[]>;
  itemKey: (item: T) => string;
  renderItem: (item: T) => ReactNode;
  onPick: (item: T) => void;
  // Khoá các mục đã chọn, hiện mờ
  chosenKeys?: Set<string>;
  buttonProps?: ButtonProps;
};

// Nút mở danh sách có ô tìm (gõ để lọc, gọi API sau 300ms); mở ra là tải ngay danh sách đầu
export const SearchPicker = <T,>({
  buttonLabel,
  searchPlaceholder,
  search,
  itemKey,
  renderItem,
  onPick,
  chosenKeys,
  buttonProps,
}: SearchPickerProps<T>) => {
  const [keyword, setKeyword] = useState("");
  const [results, setResults] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [opened, setOpened] = useState(false);
  const requestRef = useRef(0);
  // Giữ hàm tìm mới nhất trong ref để cha truyền hàm viết tại chỗ cũng không gọi lại API mỗi lần render
  const searchRef = useRef(search);
  searchRef.current = search;
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setKeyword("");
      setOpened(false);
    },
    onDropdownOpen: () => {
      combobox.focusSearchInput();
      setOpened(true);
    },
  });

  useEffect(() => {
    if (!opened) return;
    const requestId = ++requestRef.current;
    setLoading(true);
    const timer = window.setTimeout(() => {
      searchRef.current(keyword.trim())
        .then((found) => {
          if (requestId !== requestRef.current) return;
          setResults(found);
          setFailed(false);
        })
        .catch(() => {
          if (requestId !== requestRef.current) return;
          setResults([]);
          setFailed(true);
        })
        .finally(() => requestId === requestRef.current && setLoading(false));
    }, 300);
    return () => window.clearTimeout(timer);
  }, [keyword, opened]);

  return (
    <Combobox
      store={combobox}
      width={380}
      position="bottom-start"
      withinPortal
      onOptionSubmit={(key) => {
        const picked = results.find((item) => itemKey(item) === key);
        if (picked) onPick(picked);
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target withAriaAttributes={false}>
        <Button
          size="xs"
          variant="default"
          leftSection={<IconPlus size={14} />}
          onClick={() => combobox.toggleDropdown()}
          {...buttonProps}
        >
          {buttonLabel}
        </Button>
      </Combobox.Target>
      <Combobox.Dropdown>
        <Combobox.Search
          value={keyword}
          onChange={(e) => setKeyword(e.currentTarget.value)}
          placeholder={searchPlaceholder}
          rightSection={loading ? <Loader size={14} /> : undefined}
        />
        <Combobox.Options>
          <ScrollArea.Autosize mah={320} type="auto">
            {results.length > 0 ? (
              results.map((item) => {
                const key = itemKey(item);
                return (
                  <Combobox.Option value={key} key={key} disabled={chosenKeys?.has(key)}>
                    {renderItem(item)}
                  </Combobox.Option>
                );
              })
            ) : (
              <Combobox.Empty>
                {loading ? "Đang tải..." : failed ? "Không gọi được API" : "Không có kết quả"}
              </Combobox.Empty>
            )}
          </ScrollArea.Autosize>
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
};

// Dòng hiển thị trong danh sách chọn: ảnh nhỏ + tên + dòng phụ
export const PickerRow = ({
  image,
  title,
  sub,
  chosen,
}: {
  image: string | null;
  title: string;
  sub?: string;
  chosen?: boolean;
}) => (
  <Group gap={10} wrap="nowrap">
    <span className={classes.productThumb} aria-hidden>
      {image ? <img src={image} alt="" /> : <IconPhoto size={16} />}
    </span>
    <Box miw={0} style={{ flex: "1 1 auto" }}>
      <Text fz={13} truncate>
        {title}
      </Text>
      {(sub || chosen) && (
        <Text fz={12} className={classes.muted} truncate>
          {[sub, chosen ? "Đã chọn" : ""].filter(Boolean).join(" · ")}
        </Text>
      )}
    </Box>
  </Group>
);
